import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { get } from 'lodash-es';
import axios from 'axios';
import ReactLoading from 'react-loading';
import { Button, Col, Row } from 'reactstrap';
import moment from 'moment-timezone';
import update from 'immutability-helper';

import Step6Table from './Step6Table';
import Step6Banners from './Step6Banners';
import SessionDetails from './SessionDetails';
import SessionDetailsBin from './SessionDetailsBin';
import CoverPhotoModal from './CoverPhotoModal';
import PublishModal from './PublishModal';
import SponsoredLotsModal from './SponsoredLotsModal';
import FeaturedItems from './FeaturedItems';
import ConfirmModal from '../../../utilities/form-components/ConfirmModal';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import { catalog2HrWarning, catalogNoEdit } from '../../../utilities/StaticText';
import { myAuctionPath } from '../../../utilities/UrlPaths';
import { handleFilterCheckbox } from '../diy-utils/checkboxHandlers';
import { shippingApi } from '../../../api/shipping';
import { linkToStep } from '../utilities/linkToStep';

class Step6 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 0,
      error: false,
      loading: true,
      coverPhotoError: false,
      coverPhotoModal: {
        show: false,
        lot: null,
      },
      deleteLotModal: {
        show: false,
        lot: null,
      },
      deleteBulkModal: {
        show: false,
        lots: null,
      },
      dropdownCheck: {
        items: false,
        banned: false,
        potentiallyBanned: false,
        unpublished: false,
        restricted: false,
        images: false,
      },
      publishBulkModal: {
        show: false,
        lots: null,
      },
      selectedLots: new Set(),
      showPublishAllModal: false,
      showPublishedAlert: '',
      showSponLotsModal: false,
      disablePublish: false,
      enableFeaturedItems: true,
      filterList: [],
      goToPageString: '',
    };
  }

  async componentDidMount() {
    const {
      match: {
        params: { catalogID, groupID, goToPage }
      },
      house: { houseID, isFeaturedLotsEnabled },
    } = this.props;

    /**
     * (bnikom) fetch catalog data
     * pureList: unmutated lot data
     * filterList: lot data with params (if they exist)
     */
    try {
      const urls = [
        `/cps/app/group/${groupID}/buypremsteps`,
        `/cps/app/catalog/${catalogID}/getcoverimageurl`,
        `/app/isCatalogPublished/${catalogID}`,
        `/cps/app/catalog/${catalogID}/orderbids`,
        `/cps/app/catalog/${catalogID}/channels`,
        `/cps/app/catalog/${catalogID}/summary`,
        `/app/catalogURL/${catalogID}`,
        `/cps/app/catalog/${catalogID}/saleTimeIn2Hours`,
        `/cps/app/catalog/${catalogID}/house/${houseID}/primary-imageurls`,
      ];

      const [
        buyPremiumResponse,
        coverPhotoResponse,
        isPublishedResponse,
        orderBidResponse,
        postSaleResponse,
        sessionResponse,
        shareCatalogUrlResponse,
        twoHrResponse,
        coverPhotoListResponse
      ] = await Promise.all(urls.map(url => axios.get(url)
        .catch(error => console.log('this is definitely an error: ', error))));

      // Error: No published lots with images available for cover image
      const coverPhotoError = get(coverPhotoResponse, 'data.errors[0].errorCode', null) === 130;

      const {
        deletedlots,
        images,
        imagesInvNumber,
        lots,
        restrictedlots,
        unpublishedlots,
        deletedLotsWithInventoryNumber,
        unpublishedlotsWithInvNumber,
      } = sessionResponse.data.data;

      let shippingLocation;
      const originId = sessionResponse.data.data.sessionDefinition.defaultShippingOriginID;
      if (originId) {
        const response = await shippingApi.getShippingOriginById(houseID, 'AUCTIONHOUSE', originId);
        shippingLocation = get(response, 'data.origin', []);
      }
      const pureList = this.aggregateData({
        deletedlots,
        images,
        imagesInvNumber,
        lots,
        orderbids: orderBidResponse.data.data.orderbids,
        restrictedlots,
        unpublishedlots,
        deletedLotsWithInventoryNumber,
        unpublishedlotsWithInvNumber,
      });

      this.setState({
        buyPremiums: buyPremiumResponse.data.data.buypremsteps,
        coverPhoto: coverPhotoResponse.data.data.cover,
        coverPhotoError,
        coverPhotoList: coverPhotoListResponse.data.data,
        deletedLots: sessionResponse.data.data.deletedlots,
        imageCount: sessionResponse.data.data.imagecount,
        imageCountInvNumber: sessionResponse.data.data.imagecountInvNumber,
        isPublished: isPublishedResponse.data.data.isPublished,
        filterList: pureList,
        lotsWithoutImages: sessionResponse.data.data.lotswithoutimages,
        postSaleChannels: postSaleResponse.data.data.channels,
        pureList,
        isShippingEnabled: !!this.props.policy.id,
        restrictedLots: sessionResponse.data.data.restrictedlots,
        sessionDetails: sessionResponse.data.data.sessionDefinition,
        shareCatalogUrlMap: shareCatalogUrlResponse.data.data.catalogURLMap,
        shippingLocations: shippingLocation,
        twoHrsPassed: (twoHrResponse.data.data.in2Hours === 1),
        unpublishedLots: sessionResponse.data.data.unpublishedlots,
        isUnlottedItems: this.validateLotNumber(pureList),
        loading: false,
        deletedLotsWithInventoryNumber,
        unpublishedlotsWithInvNumber,
        enableFeaturedItems: isFeaturedLotsEnabled,
        goToPageString: goToPage,
      });

      this.handleDisablePublish(pureList);
    } catch (error) {
      console.log('the world may end: ', error);
      this.setState({
        error,
        loading: false,
      });
    }
  }

  componentDidUpdate(_, prevState) {
    if (prevState.showPublish !== this.state.showPublish) {
      this.handleDisablePublish();
    }
  }

  /**
   * (bnikom) aggregating different objects in the summary API cause it's bloated
   * and i have neither the patience nor want
   * adding:
   * 1. # of images each lot has
   * 2. if lot is restricted
   * 3. if lot is deleted
   * 4. if lot is published
   * 5. if there is a absentee/order bid on a lot
   */
  aggregateData = ({ deletedlots, images, imagesInvNumber, lots, orderbids, restrictedlots, unpublishedlots, deletedLotsWithInventoryNumber, unpublishedlotsWithInvNumber }) => {
    const handleAggregateDataDeleteLot = (lotNum = '', invNumber = '') => {
      const lotsWithLotNumber = Object.values(deletedlots).includes(lotNum);
      const lotsWithInventoryNumber = invNumber && Object.values(deletedLotsWithInventoryNumber).includes(invNumber);
      return lotsWithLotNumber || lotsWithInventoryNumber;
    };

    lots.forEach(lot => {
      const { lotNumber, lotExt, invNumber } = lot;
      const lotNum = `${lotNumber}${lotExt}`;
      lot.deleted = handleAggregateDataDeleteLot(lotNum, invNumber);
      lot.images = (images[lotNum]) ? images[lotNum] : imagesInvNumber[invNumber];

      if (images[lotNum]) {
        lot.images = images[lotNum];
      } else if (imagesInvNumber[invNumber]) {
        lot.images = imagesInvNumber[invNumber];
      } else {
        lot.images = 0;
      }

      lot.orderbid = (Object.values(orderbids).includes(lotNum));
      lot.unpublished = (Object.values(unpublishedlots).includes(lotNum) || Object.values(unpublishedlotsWithInvNumber).includes(invNumber));
      lot.restricted = (Object.values(restrictedlots).includes(lotNum));
    });
    return lots;
  }

  handlePageClick = data => {
    this.setState({ currentPage: data.selected, goToPageString: '' });
  }

  // filter lots by type - if images filter, filter by lots with more than 0 images
  // if lot is deleted, do not add to filtered list
  // if lot is unlotted, filter by lots without lot number but with inventory number
  // default: do not filter anything and return pure list of lots
  handleFilterPureList = (type, pureList) => {
    switch (type) {
      case 'images':
        return pureList.filter(lot => lot[type] <= 0 && !lot.deleted);
      case 'banned':
        return pureList.filter(lot => lot[type] && !lot.deleted);
      case 'potentiallyBanned':
        return pureList.filter(lot => lot[type] && !lot.deleted);
      case 'restricted':
        return pureList.filter(lot => lot[type] && !lot.deleted);
      case 'unpublished':
        return pureList.filter(lot => lot[type] && !lot.deleted && lot.unpublished);
      case 'unlotted':
        return pureList.filter(lot => !lot.lotNumber && lot.invNumber && !lot.deleted);
      default:
        return pureList;
    }
  }

  handleBannerFilter = (type, clear = false) => {
    const { pureList } = this.state;

    if (clear) {
      // (bnikom) reset lots
      this.setState({ filterList: pureList });
    } else {
      const bannerList = this.handleFilterPureList(type, pureList);
      this.setState({ filterList: bannerList, currentPage: 0 });
    }
  }

  /************************************************************************
   * MODAL FUNCTIONS (publish, delete, sponsored) BEGINNING
   ***********************************************************************/

  fetchLots = async (previousAction = '') => {
    try {
      const {
        house: { houseID },
        match: {
          params: { catalogID, type }
        },
      } = this.props;

      const isBin = type.toLowerCase() === 'bin';

      if (this.state.isShippingEnabled ) {
        await axios.post(
          `/cps/app/catalog/${catalogID}/shipping-review`,
          undefined,
          {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });
      };

      const requests = [
        axios(`/cps/app/catalog/${catalogID}/summary`),
        axios(`/cps/app/catalog/${catalogID}/house/${houseID}/primary-imageurls`)
      ];

      const [sessionResponse, coverPhotoListResponse] = await Promise.all(
        requests
      );

      const {
        deletedlots,
        images,
        imagesInvNumber,
        lots,
        lotswithoutimages,
        restrictedlots,
        sessionDefinition,
        unpublishedlots,
        deletedLotsWithInventoryNumber,
        unpublishedlotsWithInvNumber,
      } = sessionResponse.data.data;

      const pureList = this.aggregateData({
        deletedlots,
        images,
        imagesInvNumber,
        lots,
        orderbids: [],
        restrictedlots,
        unpublishedlots,
        deletedLotsWithInventoryNumber,
        unpublishedlotsWithInvNumber,
      });

      this.setState({
        coverPhotoList: coverPhotoListResponse.data.data,
        deletedLots: deletedlots,
        filterList: pureList,
        lotsWithoutImages: lotswithoutimages,
        pureList,
        restrictedLots: restrictedlots,
        selectedLots: new Set(),
        sessionDetails: sessionDefinition,
        unpublishedLots: unpublishedlots,
        loading: false,
        showPublishedAlert: isBin ? previousAction : '',
        deletedLotsWithInventoryNumber,
        unpublishedlotsWithInvNumber,
      }, () => {
        if (previousAction === 'publish') {
          window.scrollTo(0, 0);
        }
      });
    } catch (error) {
      console.log('bummer, this did not work: ', error);
      this.setState({
        error,
        loading: false,
      });
    }
  }

  toggleCoverPhotoModal = () => {
    this.setState({
      coverPhotoModal: {
        ...this.state.coverPhotoModal,
        show: !this.state.coverPhotoModal.show,
      }
    });
  }

  toggleDeleteBulkModal = (lots = null) => {
    this.setState({
      deleteBulkModal: {
        show: !this.state.deleteBulkModal.show,
        lots,
      }
    });
  }

  toggleDeleteLotModal = (lotNumber = null, lotExt = null, invNumber = null) => {
    this.setState({
      deleteLotModal: {
        show: !this.state.deleteLotModal.show,
        lot: { lotNumber, lotExt, invNumber },
      }
    });
  }

  togglePublishAllModal = () => {
    this.setState({ showPublishAllModal: !this.state.showPublishAllModal });
  }

  togglePublishBulkModal = (lots = null) => {
    this.setState({
      publishBulkModal: {
        show: !this.state.publishBulkModal.show,
        lots,
      }
    });
  }

  toggleSponLotsModal = () => {
    this.setState({ showSponLotsModal: !this.state.showSponLotsModal });
  }

  deleteLot = async () => {
    const {
      deleteLotModal: { lot: { lotNumber, lotExt, invNumber } },
      sessionDetails: { catalogID, houseID, houseAlias }
    } = this.state;

    let lotPost = {
      lotNumber,
      lotExt,
      catalogID,
      houseID,
      houseAlias,
    };

    if (!lotNumber) {
      delete lotPost.lotNumber;
      lotPost = { ...lotPost, inventoryNumber: invNumber };
    }

    try {
      await axios.post('/cps/app/lot/delete', lotPost);

      await this.fetchLots();
    } catch (error) {
      this.setState({
        error,
        loading: false,
      });
    }
  }

  deleteBulk = async () => {
    const {
      deleteBulkModal: { lots },
      sessionDetails: { catalogID },
      pureList,
    } = this.state;

    try {
      const lotArray = pureList.filter(lot => lots.has(lot.lotID));

      const arrLotNumbers = lotArray
        .map(lot => (lot.lotNumber !== '' && lot.lotNumber))
        .filter(x => x !== false && x);
      const arrInvNumbers = lotArray
        .map(lot => (!lot.lotNumber && lot.invNumber !== '' && lot.invNumber))
        .filter(x => x !== false && x);

      if (arrLotNumbers) {
        await axios.post(`/cps/app/catalog/${catalogID}/lots/delete`, arrLotNumbers);
      }
      if (arrInvNumbers) {
        await axios.post(`/cps/app/catalog/${catalogID}/lots/deleteByInventoryNumber`, arrInvNumbers);
      }

      await this.fetchLots();
    } catch (error) {
      this.setState({
        error,
        loading: false,
      });
    }
  }

  /**
  * send a request to publish ALL lots, then fetch the updated lots,
  * and update state with updated lots
  */
  publishAll = async () => {
    try {
      const {
        match: {
          params: { catalogID }
        },
      } = this.props;

      // this should be a PATCH request, but CPS is... trying its best...
      const publishRes = await axios.get(`/cps/app/catalog/${catalogID}/publishlots`);

      const { coverPhotoError } = this.state;

      if (coverPhotoError) {
        // BE does magic to set a default photo
        await axios(`/cps/app/catalog/${catalogID}/setcoverimage?_=${new Date().getTime()}`);
      }

      await this.fetchLots('publish');
    } catch (error) {
      console.log('the post office is closed: ', error);
      this.setState({
        error,
        loading: false,
      });
    }
  }

  publishBulk = async () => {
    const {
      coverPhotoError,
      publishBulkModal: { lots },
      sessionDetails: { catalogID },
      pureList,
    } = this.state;

    try {
      let lotArray = pureList.map(lot => {
        if (lots.has(lot.lotID)) {
          if (lot.lotNumber !== '' && lot.lotExt !== '') {
            return `${lot.lotNumber}${lot.lotExt}`;
          }
          if (lot.lotNumber !== '') {
            return lot.lotNumber;
          }
        }
        return null;
      });
      lotArray = lotArray.filter(x => x !== null);

      await axios.post(`/cps/app/catalog/${catalogID}/lots/publish`, lotArray);

      if (coverPhotoError) {
        // BE does magic to set a default photo
        await axios(`/cps/app/catalog/${catalogID}/setcoverimage?_=${new Date().getTime()}`);
      }

      await this.fetchLots('publish');
    } catch (error) {
      this.setState({
        error,
        loading: false,
      });
    }
  }

  selectCoverPhoto = (lot) => {
    this.setState({
      coverPhotoModal: {
        ...this.state.coverPhotoModal,
        lot
      },
      coverPhotoError: false
    });
  }

  postCoverPhoto = async () => {
    try {
      const {
        match: {
          params: { catalogID }
        },
      } = this.props;

      const { coverPhotoModal: { lot } } = this.state;
      await axios.post(`/cps/app/catalog/${catalogID}/selectcoverimage/`, { httpThumbPath: lot });

      const coverPhotoResponse = await axios.get(`/cps/app/catalog/${catalogID}/getcoverimageurl`);
      this.setState({ coverPhoto: coverPhotoResponse.data.data.cover });
    } catch (error) {
      console.log('no new cover photo :(((( ', error);
      this.setState({
        error,
        loading: false,
      });
    }
  }

  /************************************************************************
   * MODAL FUNCTIONS END
   ***********************************************************************/

  /************************************************************************
  * STEP 6 TABLE FUNCTIONS BEGIN
  ***********************************************************************/

  handleSelectedLots = (lots) => {
    this.setState({ selectedLots: lots });
  }

  handleDropdownMessage = (type) => {
    const { dropdownCheck, selectedLots, pureList } = this.state;

    let newState = dropdownCheck;

    /**
     * (bnikom) if the state of the item is true (aka selected)
     * OR the key is not the selected item
     * set the state of the item to false = ! (aka not selected)
     */
    Object.keys(newState).forEach(key => {
      newState = update(newState, {
        $merge: {
          [key]: !(dropdownCheck[type] || key !== type),
        }
      });
    });

    this.setState({
      dropdownCheck: newState,
      selectedLots: handleFilterCheckbox(pureList, selectedLots, type, dropdownCheck[type])
    });
  }

  // Return true if lots does not contain lot.lotNumber
  validateLotNumber = lots => {
    const lotFound = lots.find(lot => !lot.lotNumber);
    return !lotFound;
  }

  /**
   * @function
   * @param {Array<Any>?} optionalList
   */
  handleDisablePublish = (optionalList) => {
    const { filterList } = this.state;

    const listToValidate = optionalList || filterList;
    const isValid = this.validateLotNumber(listToValidate);
    if (isValid) {
      this.setState({ disablePublish: false });
    } else {
      this.setState({ disablePublish: true });
    }
  }

  submitStepSix = async option => {
    const {
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    const isBin = type.toLowerCase() === 'bin';
    const stepNumber = isBin ? 6 : 7;

    await axios.post(`/cps/app/diy-step-tracker/${catalogID}`, stepNumber, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (option === 'continue') {
      this.props.history.push(
        `/session/${type}/${parseInt(groupID, 10)}/${catalogID}/${stepNumber}`
      );
    } else {
      this.props.history.push(myAuctionPath);
    }
  };
  /************************************************************************
   * STEP 6 TABLE FUNCTIONS END
   ***********************************************************************/

  render() {
    const { loading, error, disablePublish } = this.state;
    if (loading) {
      return (
        <ReactLoading className="loading-spinner" type="spin" color="black" />
      );
    } if (error) {
      return (
        <InlineAlert />
      );
    }

    const {
      match: {
        params: { type }
      }
    } = this.props;

    const {
      buyPremiums,
      coverPhoto,
      coverPhotoList,
      coverPhotoModal,
      currentPage,
      deleteBulkModal,
      deleteLotModal,
      dropdownCheck,
      imageCount,
      imageCountInvNumber,
      isPublished,
      isShippingEnabled,
      filterList,
      postSaleChannels,
      publishBulkModal,
      pureList,
      selectedLots,
      sessionDetails,
      shippingLocations,
      shareCatalogUrlMap,
      showPublishAllModal,
      showPublishedAlert,
      showSponLotsModal,
      twoHrsPassed,
      unpublishedLots,
      isUnlottedItems,
      goToPageString,
    } = this.state;

    const isBin = type.toLowerCase() === 'bin';
    const lotLabelUppercase = isBin ? 'Item' : 'Lot';
    const lotLabelLowercase = isBin ? 'item' : 'lot';
    const selectedLotsNum = selectedLots.size;
    const selectedLotsPlural = selectedLotsNum > 1;

    const { isTimed, TZ_zoneName: timezoneName } = sessionDetails;
    const unpublishedLotsNum = unpublishedLots.length;
    const showPublishButton = !isPublished || unpublishedLotsNum > 0;
    const redirectPage = goToPageString ? goToPageString : currentPage;

    let startTime = null;
    let endTime = null;
    if (isTimed === 1) {
      // (bnikom) date format for modal publish
      // isTimed === 1: true, isTimed === 0: false (live)
      const dateFormat = (time) => moment.utc(time).tz(timezoneName).format('MMMM Do YYYY, h:mm A z');

      const unpublishedLotList = pureList.filter(lot => lot.status === 's');

      startTime = unpublishedLotList.length > 0
        && dateFormat(unpublishedLotList[0].endTimeUTCString);
      endTime = unpublishedLotList.length > 0
        && dateFormat(unpublishedLotList[unpublishedLotList.length - 1].endTimeUTCString);
    }

    /**
     * (bnikom) every time a lot is deleted it is also removed from the unpublishedlot BE response
     * which kinda makes sense, and also doesn't make sense -
     * this makes it hard to get the total unpublished + deleted lots.
     * so this calculates the true # of unpublished and deleted lots. tada.
     */
    const totalUnpubDelLots = filterList.reduce((total, value) => (
      value.unpublished || value.deleted ? ++total : total), 0);

    const {
      sessionDetails: { catalogID },
    } = this.state;

    return (
      <div className="pb-5">
        {showPublishedAlert === 'publish' && (
          <InlineAlert
            baseText="Items may take 24-48 hours to appear on Invaluable.com. Please contact customer care if they are not showing after that time."
            color="success"
            icon="check-circle"
            titleText="Your items were successfully published!"
            showPhones={false}
          />
        )}
        {twoHrsPassed
          && (
          <InlineAlert
            baseText={catalogNoEdit}
            color="warning"
            icon="clock"
            titleText={catalog2HrWarning}
            showPhones={false}
            uncontrolled={true}
          />
          )}
        {!twoHrsPassed && sessionDetails.creator === 'resell'
          && (
          <InlineAlert
            titleText="Review Session Information"
            baseText={`Some of the session information has been copied from the original
            live auction. Please review all session information before publishing.`}
            color="warning"
            icon="exclamation-triangle"
          />
          )}
        {isBin ? (
          <SessionDetailsBin
            isPublished={isPublished}
            linkToStep={linkToStep}
            sessionDetails={sessionDetails}
            shareCatalogUrlMap={shareCatalogUrlMap}
          />
        ) : (
          <SessionDetails
            buyPremiums={buyPremiums}
            coverPhoto={coverPhoto}
            isPublished={isPublished}
            linkToStep={linkToStep}
            postSaleChannels={postSaleChannels}
            sessionDetails={sessionDetails}
            shippingLocations={shippingLocations}
            isShippingEnabled={isShippingEnabled}
            shareCatalogUrlMap={shareCatalogUrlMap}
            toggleCoverPhotoModal={this.toggleCoverPhotoModal}
            twoHrsPassed={twoHrsPassed}
          />
        )}
        {this.state.enableFeaturedItems && (
          <FeaturedItems
            lotList={pureList}
            coverPhotoList={coverPhotoList}
            catalogID={catalogID}
          />
        )}
        <Step6Banners
          handleBannerFilter={this.handleBannerFilter}
          lengthCheck={filterList.length === pureList.length}
          // (bnikom) as soon as you catch an item that is banned/potenBan, stop and set to true
          isBanned={pureList.some(lot => lot.banned && !lot.deleted)}
          isPotentiallyBanned={pureList.some(lot => lot.potentiallyBanned && !lot.deleted)}
          isRestricted={pureList.some(lot => lot.restricted && !lot.deleted)}
          isUnpublished={pureList.some(lot => lot.unpublished && !lot.deleted)}
          noImage={pureList.some(lot => lot.images <= 0 && !lot.deleted)}
          isUnlottedItems={isUnlottedItems}
        />
        <Step6Table
          areUnpublishedLots={unpublishedLots.length > 0}
          currentPage={redirectPage}
          dropdownCheck={dropdownCheck}
          handleBannerFilter={this.handleBannerFilter}
          handleDropdownMessage={this.handleDropdownMessage}
          handlePageClick={this.handlePageClick}
          handleSelectedLots={this.handleSelectedLots}
          isTimed={sessionDetails.isTimed}
          isBanned={pureList.some(lot => lot.banned && !lot.deleted)}
          isCatalogPublished={isPublished}
          isPotentiallyBanned={pureList.some(lot => lot.potentiallyBanned && !lot.deleted)}
          isRestricted={pureList.some(lot => lot.restricted && !lot.deleted)}
          isUnpublished={pureList.some(lot => lot.unpublished && !lot.deleted)}
          // (bnikom) if the number of deleted + unpublished lots changes, re-render table
          key={totalUnpubDelLots}
          lots={filterList}
          noImage={pureList.some(lot => lot.images <= 0 && !lot.deleted)}
          numImages={imageCount}
          numImagesInvNumber={imageCountInvNumber}
          numLots={filterList.length}
          selectedLots={selectedLots}
          toggleDeleteBulkModal={this.toggleDeleteBulkModal}
          toggleDeleteLotModal={this.toggleDeleteLotModal}
          togglePublishAllModal={this.togglePublishAllModal}
          togglePublishBulkModal={this.togglePublishBulkModal}
          twoHrsPassed={twoHrsPassed}
          unpublishedLotsNum={unpublishedLotsNum}
        />
        <Row className="mt-4">
          {showPublishButton
            && (
            <Col xs={2}>
              <Button
                className="w-100"
                color="primary"
                onClick={() => this.togglePublishAllModal()}
                disabled={disablePublish}
              >
                Publish All
              </Button>
            </Col>
            )}
          {!!this.props.policy.id && <Col xs={2} className="d-flex align-items-center">
            <Button
              role="link"
              className="w-100"
              color="primary"
              onClick={() => this.submitStepSix('continue')}
            >
              Continue
            </Button>
          </Col>}
          <Col xs={2} className="d-flex align-items-center">
            <Link to={myAuctionPath}>
              Save &amp; Exit
            </Link>
          </Col>
        </Row>
        <CoverPhotoModal
          coverPhoto={coverPhoto}
          coverPhotoList={coverPhotoList}
          coverPhotoModal={coverPhotoModal}
          postCoverPhoto={this.postCoverPhoto}
          selectCoverPhoto={this.selectCoverPhoto}
          toggleFunction={this.toggleCoverPhotoModal}
        />
        {/* Publish Selected Items Modal */}
        <PublishModal
          end={endTime}
          isBin={isBin}
          isTimed={isTimed}
          publishFunction={this.publishBulk}
          showPublish={publishBulkModal.show}
          start={startTime}
          text={`${selectedLotsNum} ${lotLabelLowercase}${selectedLotsPlural ? 's' : ''}`}
          titleText={`Publish ${lotLabelUppercase}${selectedLotsPlural ? 's' : ''}`}
          togglePublishModal={this.togglePublishBulkModal}
          toggleSponLotModal={this.toggleSponLotsModal}
        />
        {/* Publish All Items Modal */}
        <PublishModal
          end={endTime}
          isBin={isBin}
          isTimed={isTimed}
          publishFunction={this.publishAll}
          showPublish={showPublishAllModal}
          start={startTime}
          text={`${unpublishedLotsNum} ${lotLabelLowercase}${unpublishedLotsNum !== 1 ? 's' : ''}`}
          titleText={`Publish ${lotLabelUppercase}${unpublishedLotsNum !== 1 ? 's' : ''}`}
          togglePublishModal={this.togglePublishAllModal}
          toggleSponLotModal={this.toggleSponLotsModal}
        />
        <ConfirmModal
          actionFunction={this.deleteLot}
          bodyText={`Are you sure you want to delete this ${lotLabelLowercase}? You cannot reuse this ${lotLabelLowercase} number in this session.`}
          buttonText="Delete"
          showModal={deleteLotModal.show}
          title={`Delete ${lotLabelUppercase}`}
          toggleFunction={this.toggleDeleteLotModal}
        />
        <ConfirmModal
          actionFunction={this.deleteBulk}
          bodyText={`Are you sure you want to delete ${selectedLotsPlural ? 'these' : 'this'} ${selectedLotsPlural ? selectedLots.size : ''} ${lotLabelLowercase}${selectedLotsPlural ? 's' : ''}? You cannot reuse these ${lotLabelLowercase} numbers in this session.`}
          buttonText="Delete"
          showModal={deleteBulkModal.show}
          title={`Delete ${selectedLotsPlural ? selectedLots.size : ''} ${lotLabelUppercase}${selectedLotsPlural ? 's' : ''}`}
          toggleFunction={this.toggleDeleteBulkModal}
        />
        <SponsoredLotsModal
          isBin={isBin}
          toggleFunction={this.toggleSponLotsModal}
          showModal={showSponLotsModal}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ house, user, policy }) => ({
  house,
  user,
  policy
});

export default withRouter(
  connect(
    mapStateToProps,
    null
  )(Step6)
);
